import React, { useEffect, useMemo, useState } from 'react';
import { collection, deleteDoc, doc, onSnapshot, query, setDoc } from 'firebase/firestore';
import { useLocation, useNavigate } from 'react-router-dom';
import { db } from '../../../../shared/storage/firebase';
import type { OpsCheckpointCategory, OpsCheckpointDefinition, OpsTemplateTask } from '../../types';
import { ACTIVE_OPS_LOGISTICS_CATALOG as OPS_LOGISTICS_CATALOG } from '../../catalog/activeLogisticsCatalog';
import SearchableSelect from '../shared/SearchableSelect';
import LogisticsMultiPickModal from '../shared/LogisticsMultiPickModal';
import { slugify } from '../../../../shared/utils/slugify';

function safeString(v: any): string {
  return String(v || '').trim();
}

function parseTags(raw: string): string[] {
  return raw
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
}

function stripUndefinedDeep<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map(v => stripUndefinedDeep(v)) as any;
  }
  if (value && typeof value === 'object') {
    const out: any = {};
    for (const [k, v] of Object.entries(value as any)) {
      if (v === undefined) continue;
      out[k] = stripUndefinedDeep(v);
    }
    return out;
  }
  return value;
}

export default function CheckpointsTab(): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const preselectId = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return safeString(sp.get('id'));
  }, [location.search]);

  const [checkpoints, setCheckpoints] = useState<OpsCheckpointDefinition[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [dragTaskKey, setDragTaskKey] = useState<null | { tIdx: number }>(null);
  const [dragOverTaskKey, setDragOverTaskKey] = useState<null | { tIdx: number }>(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingTaskTitleId, setEditingTaskTitleId] = useState<string | null>(null);
  const [addTasksModalOpen, setAddTasksModalOpen] = useState(false);
  const [addTasksSelectedLogisticIds, setAddTasksSelectedLogisticIds] = useState<string[]>([]);

  const logisticOptions = useMemo(
    () => OPS_LOGISTICS_CATALOG.map(l => ({ id: l.id, label: l.label })),
    []
  );

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'opsCheckpoints')), (snap) => {
      const list = snap.docs.map(d => ({ ...(d.data() as any), checkpointId: d.id } as OpsCheckpointDefinition));
      list.sort((a, b) => String(a.title || a.checkpointId).localeCompare(String(b.title || b.checkpointId)));
      setCheckpoints(list);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (preselectId) setSelectedId(preselectId);
  }, [preselectId]);

  useEffect(() => {
    setError(null);
    if (!selectedId) {
      setDraft(null);
      return;
    }
    const c = checkpoints.find(x => x.checkpointId === selectedId) || null;
    setDraft(() => {
      if (!c) return null;
      const next = JSON.parse(JSON.stringify(c));
      // Keep a user-editable tagsText field for the UI; tags array is derived on save.
      if (next && typeof next === 'object' && next.tagsText == null) {
        next.tagsText = Array.isArray(next.tags) ? next.tags.join(', ') : '';
      }
      return next;
    });
  }, [checkpoints, selectedId]);

  const nowMs = () => Date.now();

  const normalizeCheckpoint = (c: any) => {
    const tasks = Array.isArray(c?.tasks) ? c.tasks : [];
    const nextTasks = tasks.map((t: any, idx: number) => ({
      ...t,
      order: idx,
      required: t?.required ?? true,
    }));
    const tags = parseTags(
      String(
        c?.tagsText ??
          (Array.isArray(c?.tags) ? c.tags.join(', ') : '')
      )
    );
    return { ...c, tasks: nextTasks, tags };
  };

  const newTask = (checkpointId: string, title: string, order: number): OpsTemplateTask => {
    const slug = slugify(title || 'task');
    const taskId = `${checkpointId}.${slug || 'task'}`.replace(/\.{2,}/g, '.');
    return { taskId, title: title || 'New task', order, required: true };
  };

  const sanitizeIdForTask = (id: string) => String(id || '').trim().replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');

  const addLogisticsAsTasks = (checkpointId: string, logisticIds: string[]) => {
    setDraft((d: any) => {
      const tasks = Array.isArray(d.tasks) ? [...d.tasks] : [];
      const existingLogisticIds = new Set(tasks.map((t: any) => String(t.logisticId || '').trim()).filter(Boolean));
      const existingTaskIds = new Set(tasks.map((t: any) => String(t.taskId || '').trim()).filter(Boolean));

      for (const logisticIdRaw of logisticIds) {
        const logisticId = String(logisticIdRaw || '').trim();
        if (!logisticId) continue;
        if (existingLogisticIds.has(logisticId)) continue;
        const def = OPS_LOGISTICS_CATALOG.find(x => String(x.id) === logisticId);
        const title = String(def?.label || logisticId);
        const taskId = `${checkpointId}.li.${sanitizeIdForTask(logisticId)}`.replace(/\.{2,}/g, '.');
        if (existingTaskIds.has(taskId)) continue;
        tasks.push({
          taskId,
          title,
          order: tasks.length,
          required: true,
          logisticId,
        });
        existingLogisticIds.add(logisticId);
        existingTaskIds.add(taskId);
      }

      return normalizeCheckpoint({ ...d, tasks });
    });
  };

  const createNew = async () => {
    setError(null);
    const title = 'New checkpoint';
    const base = `cp.${slugify(title).replace(/-/g, '') || 'checkpoint'}`;
    const existing = new Set(checkpoints.map(c => String(c.checkpointId)));
    let id = base;
    let n = 2;
    while (existing.has(id)) {
      id = `${base}.${n}`;
      n += 1;
    }
    const t = nowMs();
    const payload: OpsCheckpointDefinition = {
      checkpointId: id,
      title,
      category: 'screening_questions',
      tags: [],
      tasks: [],
      createdAt: t,
      createdBy: '',
      updatedAt: t,
      updatedBy: '',
    };
    setSaving(true);
    try {
      // Store checkpointId in the document ID; do not write it as a field.
      const { checkpointId: _ignore, ...rest } = payload as any;
      await setDoc(doc(db, 'opsCheckpoints', id), stripUndefinedDeep(rest), { merge: true });
      setSelectedId(id);
      navigate(`/app/admin/ops/checkpoints?id=${encodeURIComponent(id)}`);
    } catch (e: any) {
      setError(String(e?.message || 'Failed to create checkpoint.'));
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      const t = nowMs();
      const payload: any = normalizeCheckpoint({ ...(draft as any), updatedAt: t });
      const checkpointId = safeString(payload.checkpointId);
      if (!checkpointId) throw new Error('checkpointId is required.');
      const ref = doc(db, 'opsCheckpoints', checkpointId);
      const { checkpointId: _ignore, tagsText: _ignore2, ...rest } = payload;
      await setDoc(ref, stripUndefinedDeep(rest), { merge: true });
    } catch (e: any) {
      setError(String(e?.message || 'Failed to save.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!draft) return;
    const ok = await window.appConfirm('Delete this checkpoint? This cannot be undone.');
    if (!ok) return;
    setSaving(true);
    setError(null);
    try {
      const checkpointId = safeString((draft as any).checkpointId);
      if (!checkpointId) return;
      await deleteDoc(doc(db, 'opsCheckpoints', checkpointId));
      setSelectedId(null);
      navigate('/app/admin/ops/checkpoints');
    } catch (e: any) {
      setError(String(e?.message || 'Failed to delete.'));
    } finally {
      setSaving(false);
    }
  };

  const duplicate = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      const t = nowMs();
      const oldId = safeString((draft as any).checkpointId);
      const title = safeString((draft as any).title) || 'Checkpoint';
      const slug = slugify(`${title} copy`).replace(/-/g, '') || 'checkpointcopy';
      const base = `cp.${slug}`;
      const existing = new Set(checkpoints.map(c => String(c.checkpointId)));
      let checkpointId = base;
      let n = 2;
      while (existing.has(checkpointId)) {
        checkpointId = `${base}.${n}`;
        n += 1;
      }

      const nextDraft: any = JSON.parse(JSON.stringify(draft));
      nextDraft.checkpointId = checkpointId;
      nextDraft.title = `${title} (copy)`;
      nextDraft.createdAt = t;
      nextDraft.updatedAt = t;
      nextDraft.createdBy = '';
      nextDraft.updatedBy = '';

      // Rewrite task IDs to use the new checkpointId prefix.
      const tasks = Array.isArray(nextDraft.tasks) ? nextDraft.tasks : [];
      nextDraft.tasks = tasks.map((task: any) => {
        const prevTaskId = safeString(task?.taskId);
        const suffix =
          oldId && prevTaskId.startsWith(`${oldId}.`)
            ? prevTaskId.slice(oldId.length)
            : `.${slugify(safeString(task?.title) || 'task') || 'task'}`;
        return { ...task, taskId: `${checkpointId}${suffix}`.replace(/\.{2,}/g, '.') };
      });

      // Ensure tags derived correctly on save; keep tagsText for UI.
      const payload: any = normalizeCheckpoint({ ...nextDraft });
      payload.tagsText = nextDraft.tagsText ?? (Array.isArray(payload.tags) ? payload.tags.join(', ') : '');

      const { checkpointId: _ignore, tagsText: _ignore2, ...rest } = payload;
      await setDoc(doc(db, 'opsCheckpoints', checkpointId), stripUndefinedDeep(rest), { merge: true });
      setSelectedId(checkpointId);
      navigate(`/app/admin/ops/checkpoints?id=${encodeURIComponent(checkpointId)}`);
    } catch (e: any) {
      setError(String(e?.message || 'Failed to duplicate.'));
    } finally {
      setSaving(false);
    }
  };

  const categoryOptions: Array<{ id: OpsCheckpointCategory; label: string }> = [
    { id: 'screening_questions', label: 'Screening Questions' },
    { id: 'lead', label: 'Lead' },
    { id: 'contract_safe', label: 'Contract Safe' },
    { id: 'teacher_assign_ready', label: 'Teacher-assign Ready' },
    { id: 'commitment_ready', label: 'Commitment Ready' },
    { id: 'week_before', label: 'Week Before' },
    { id: 'first_day', label: 'First Day' },
    { id: 'halfway_review', label: 'Halfway Review' },
    { id: 'stabilized', label: 'Stabilized' },
    // Legacy id kept for backward compatibility (display friendly label).
    { id: 'marketing', label: 'Marketing Ready' },
  ];

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
        <div>
          <h2 style={{ marginTop: 0, marginBottom: 4 }}>Checkpoints</h2>
          <div className="muted">Reusable checkpoints for templates and flows (with metadata for filtering/reporting).</div>
        </div>
      </div>

      {error ? <div className="billing-error" style={{ marginTop: 10 }}>{error}</div> : null}

      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 14, marginTop: 14 }}>
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontWeight: 800 }}>Library</div>
            <button className="mini-btn" type="button" onClick={() => void createNew()} disabled={saving}>New</button>
          </div>

          <div style={{ maxHeight: 520, overflow: 'auto' }}>
            {checkpoints.length === 0 ? (
              <div className="muted">None yet.</div>
            ) : (
              checkpoints.map((c) => {
                const isActive = c.checkpointId === selectedId;
                return (
                  <button
                    key={c.checkpointId}
                    type="button"
                    onClick={() => {
                      setSelectedId(c.checkpointId);
                      navigate(`/app/admin/ops/checkpoints?id=${encodeURIComponent(c.checkpointId)}`);
                    }}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      border: '1px solid #e5e7eb',
                      borderRadius: 10,
                      padding: 10,
                      marginBottom: 8,
                      background: isActive ? '#f3f4f6' : 'white',
                    }}
                  >
                    <div style={{ fontWeight: 700, color: '#111827' }}>{c.title || c.checkpointId}</div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{c.checkpointId}</div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 12 }}>
          {!draft ? (
            <div className="muted">Select or create a checkpoint.</div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {editingTitle ? (
                    <input
                      value={safeString((draft as any).title)}
                      autoFocus
                      onChange={(e) => setDraft((d: any) => ({ ...d, title: e.target.value }))}
                      onBlur={() => setEditingTitle(false)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                        if (e.key === 'Escape') setEditingTitle(false);
                      }}
                      style={{ fontWeight: 900, fontSize: 18 }}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setEditingTitle(true)}
                      title="Click to rename checkpoint"
                      style={{
                        fontWeight: 900,
                        fontSize: 18,
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        cursor: 'text',
                        color: '#111827',
                        textAlign: 'left',
                      }}
                    >
                      {safeString((draft as any).title) || 'Checkpoint'}
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="mini-btn" type="button" onClick={() => void duplicate()} disabled={saving}>
                    Duplicate
                  </button>
                  <button className="mini-btn danger" type="button" onClick={() => void remove()} disabled={saving}>
                    Delete
                  </button>
                  <button className="btn btn-primary" type="button" onClick={() => void save()} disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>

              <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '200px 1fr', gap: 12, alignItems: 'end' }}>
                <div>
                  <label style={{ fontSize: 12, color: '#6b7280' }}>Category</label>
                  {(() => {
                    const current = String((draft as any).category || '').trim();
                    const ids = new Set(categoryOptions.map(o => String(o.id)));
                    const showLegacy = current && !ids.has(current);
                    return (
                      <select
                        value={String((draft as any).category || categoryOptions[0]?.id || 'screening_questions')}
                        onChange={(e) => setDraft((d: any) => ({ ...d, category: e.target.value }))}
                        style={{ width: '100%' }}
                      >
                        {categoryOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                        {showLegacy ? <option value={current}>{current} (legacy)</option> : null}
                      </select>
                    );
                  })()}
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#6b7280' }}>Tags (comma separated)</label>
                  <input
                    value={String((draft as any).tagsText || '')}
                    onChange={(e) => setDraft((d: any) => ({ ...d, tagsText: e.target.value }))}
                    style={{ width: '100%' }}
                    placeholder="wifi, roster, instructor"
                  />
                </div>
              </div>

              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 800 }}>Tasks</div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <button
                    className="mini-btn"
                    type="button"
                    onClick={() => {
                      setAddTasksSelectedLogisticIds([]);
                      setAddTasksModalOpen(true);
                    }}
                  >
                    + Add logistics
                  </button>
                  <button
                    className="mini-btn"
                    type="button"
                    onClick={() => setDraft((d: any) => {
                      const tasks = Array.isArray(d.tasks) ? d.tasks : [];
                      const next = newTask(safeString(d.checkpointId), `Task ${tasks.length + 1}`, tasks.length);
                      return normalizeCheckpoint({ ...d, tasks: [...tasks, next] });
                    })}
                    title="Add a process-only task (no logistic)"
                  >
                    + Process task
                  </button>
                </div>
              </div>

              <LogisticsMultiPickModal
                open={addTasksModalOpen}
                title="Add logistics as tasks"
                logistics={OPS_LOGISTICS_CATALOG}
                selectedIds={addTasksSelectedLogisticIds}
                onChangeSelectedIds={setAddTasksSelectedLogisticIds}
                onClose={() => setAddTasksModalOpen(false)}
                onConfirm={() => {
                  const checkpointId = safeString((draft as any)?.checkpointId);
                  if (checkpointId) addLogisticsAsTasks(checkpointId, addTasksSelectedLogisticIds);
                  setAddTasksModalOpen(false);
                }}
              />

              <div style={{ marginTop: 10 }}>
                {(draft.tasks || []).length === 0 ? <div className="muted">No tasks yet.</div> : null}
              </div>

              {(draft.tasks || []).map((t: OpsTemplateTask, tIdx: number) => {
                const logisticId = String(t.logisticId || '').trim();
                const isTaskDragOver = dragOverTaskKey?.tIdx === tIdx;
                const isEditingTaskTitle = Boolean(t.taskId && editingTaskTitleId === t.taskId);
                return (
                  <div
                    key={t.taskId || tIdx}
                    style={{
                      borderTop: '1px solid #f3f4f6',
                      paddingTop: 10,
                      marginTop: 10,
                      outline: isTaskDragOver ? '2px solid #60a5fa' : 'none',
                      outlineOffset: 2,
                      borderRadius: 8,
                    }}
                    onDragOver={(e) => {
                      if (!dragTaskKey) return;
                      e.preventDefault();
                      setDragOverTaskKey({ tIdx });
                    }}
                    onDrop={(e) => {
                      if (!dragTaskKey) return;
                      e.preventDefault();
                      const from = dragTaskKey.tIdx;
                      const to = tIdx;
                      setDragTaskKey(null);
                      setDragOverTaskKey(null);
                      if (from === to) return;
                      setDraft((d: any) => {
                        const tasks = [...(d.tasks || [])];
                        const [moved] = tasks.splice(from, 1);
                        tasks.splice(to, 0, moved);
                        return normalizeCheckpoint({ ...d, tasks });
                      });
                    }}
                    onDragLeave={() => {
                      if (dragOverTaskKey?.tIdx === tIdx) setDragOverTaskKey(null);
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flex: 1, minWidth: 0 }}>
                        <button
                          type="button"
                          className="mini-btn"
                          draggable
                          onDragStart={(e) => {
                            setDragTaskKey({ tIdx });
                            setDragOverTaskKey(null);
                            try {
                              e.dataTransfer.setData('text/plain', `task:${tIdx}`);
                              e.dataTransfer.effectAllowed = 'move';
                            } catch {
                              // ignore
                            }
                          }}
                          onDragEnd={() => {
                            setDragTaskKey(null);
                            setDragOverTaskKey(null);
                          }}
                          title="Drag to reorder tasks"
                          style={{ cursor: 'grab' }}
                        >
                          ⋮⋮
                        </button>
                        {isEditingTaskTitle ? (
                          <input
                            value={safeString(t.title) || ''}
                            autoFocus
                            onChange={(e) => setDraft((d: any) => {
                              const tasks = [...(d.tasks || [])];
                              tasks[tIdx] = { ...tasks[tIdx], title: e.target.value };
                              return normalizeCheckpoint({ ...d, tasks });
                            })}
                            onBlur={() => setEditingTaskTitleId(null)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                              if (e.key === 'Escape') setEditingTaskTitleId(null);
                            }}
                            style={{ fontWeight: 800, fontSize: 16, lineHeight: '32px' }}
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() => setEditingTaskTitleId(String(t.taskId || '') || null)}
                            title="Click to rename task"
                            style={{
                              fontWeight: 800,
                              fontSize: 16,
                              lineHeight: '32px',
                              background: 'transparent',
                              border: 'none',
                              padding: '6px 0',
                              cursor: 'text',
                              color: '#111827',
                              textAlign: 'left',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: 320,
                            }}
                          >
                            {safeString(t.title) || 'Task'}
                          </button>
                        )}

                        <div style={{ width: 340, maxWidth: '40vw' }}>
                          <SearchableSelect
                            valueId={logisticId}
                            placeholder="Pick logistic (optional)…"
                            options={logisticOptions}
                            disabled={saving}
                            onSelectId={(id) => setDraft((d: any) => {
                              const tasks = [...(d.tasks || [])];
                              tasks[tIdx] = { ...tasks[tIdx], logisticId: id, title: tasks[tIdx].title || (OPS_LOGISTICS_CATALOG.find(x => x.id === id)?.label || id) };
                              return normalizeCheckpoint({ ...d, tasks });
                            })}
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        className="mini-btn danger"
                        onClick={() => setDraft((d: any) => {
                          const tasks = [...(d.tasks || [])];
                          tasks.splice(tIdx, 1);
                          return normalizeCheckpoint({ ...d, tasks });
                        })}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

